import { formatMeetingTime } from '../../utils/meetingSchedule';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Send,
  Schedule,
  CheckCircle,
  Error,
  Refresh,
  Email,
  Sms,
  WhatsApp,
  Notifications
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { schedulingService, ScheduledMeeting } from '../../services/schedulingService';

interface MeetingNotification {
  id: number;
  notification_type: string;
  channel: string;
  recipient: string;
  subject?: string;
  status: string;
  scheduled_for?: string;
  sent_at?: string;
  delivered_at?: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
}

interface MeetingNotificationsProps {
  meeting: ScheduledMeeting;
  open: boolean;
  onClose: () => void;
}

const MeetingNotifications: React.FC<MeetingNotificationsProps> = ({
  meeting,
  open,
  onClose
}) => {
  const [notifications, setNotifications] = useState<MeetingNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [newNotification, setNewNotification] = useState({
    type: 'confirmation',
    channel: 'whatsapp',
    customMessage: ''
  });

  useEffect(() => {
    if (open && meeting.id) {
      loadNotifications();
    }
  }, [open, meeting.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await schedulingService.getMeetingNotifications(meeting.id);
      setNotifications(data);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Error al cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      setSendingNotification(true);
      setError(null);

      await schedulingService.sendMeetingNotification(
        meeting.id,
        newNotification.type,
        newNotification.channel,
        newNotification.customMessage || undefined
      );

      // Refresh notifications
      await loadNotifications();
      
      setShowSendDialog(false);
      setNewNotification({
        type: 'confirmation',
        channel: 'whatsapp',
        customMessage: ''
      });
    } catch (err) {
      console.error('Error sending notification:', err);
      setError('Error al enviar la notificación');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleScheduleReminders = async () => {
    try {
      setError(null);
      await schedulingService.scheduleMeetingReminders(meeting.id);
      await loadNotifications();
    } catch (err) {
      console.error('Error scheduling reminders:', err);
      setError('Error al programar recordatorios');
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return <Email fontSize="small" />;
      case 'sms':
        return <Sms fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      default:
        return <Notifications fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return <CheckCircle fontSize="small" />;
      case 'failed':
        return <Error fontSize="small" />;
      case 'pending':
        return <Schedule fontSize="small" />;
      default:
        return <Notifications fontSize="small" />;
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: es });
  };

  const getNotificationTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'confirmation': 'Confirmación',
      'reminder': 'Recordatorio',
      'cancellation': 'Cancelación',
      'reschedule': 'Reprogramación'
    };
    return labels[type] || type;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Notificaciones - {meeting.customer_name}
            </Typography>
            <Box display="flex" gap={1}>
              <Tooltip title="Actualizar">
                <IconButton onClick={loadNotifications} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<Schedule />}
                onClick={handleScheduleReminders}
                size="small"
              >
                Programar Recordatorios
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => setShowSendDialog(true)}
                size="small"
              >
                Enviar Notificación
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Meeting Info */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Proyecto: <strong>{meeting.project_name}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Fecha: <strong>{meeting.meeting_date} {formatMeetingTime(meeting)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Teléfono: <strong>{meeting.customer_phone}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Email: <strong>{meeting.customer_email || 'No disponible'}</strong>
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Destinatario</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Programada</TableCell>
                    <TableCell>Enviada</TableCell>
                    <TableCell>Reintentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">
                          No hay notificaciones para esta cita
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell>
                          {getNotificationTypeLabel(notification.notification_type)}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getChannelIcon(notification.channel)}
                            {notification.channel.toUpperCase()}
                          </Box>
                        </TableCell>
                        <TableCell>{notification.recipient}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(notification.status)}
                            label={notification.status.toUpperCase()}
                            color={getStatusColor(notification.status)}
                            size="small"
                          />
                          {notification.error_message && (
                            <Tooltip title={notification.error_message}>
                              <Error color="error" fontSize="small" sx={{ ml: 1 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(notification.scheduled_for)}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(notification.sent_at)}
                        </TableCell>
                        <TableCell>
                          {notification.retry_count > 0 && (
                            <Chip
                              label={notification.retry_count}
                              color="warning"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={showSendDialog} onClose={() => setShowSendDialog(false)}>
        <DialogTitle>Enviar Notificación</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Notificación</InputLabel>
              <Select
                value={newNotification.type}
                onChange={(e) => setNewNotification({
                  ...newNotification,
                  type: e.target.value
                })}
                label="Tipo de Notificación"
              >
                <MenuItem value="confirmation">Confirmación</MenuItem>
                <MenuItem value="reminder">Recordatorio</MenuItem>
                <MenuItem value="cancellation">Cancelación</MenuItem>
                <MenuItem value="reschedule">Reprogramación</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select
                value={newNotification.channel}
                onChange={(e) => setNewNotification({
                  ...newNotification,
                  channel: e.target.value
                })}
                label="Canal"
              >
                <MenuItem value="whatsapp">
                  <Box display="flex" alignItems="center" gap={1}>
                    <WhatsApp fontSize="small" />
                    WhatsApp
                  </Box>
                </MenuItem>
                <MenuItem value="sms">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Sms fontSize="small" />
                    SMS
                  </Box>
                </MenuItem>
                <MenuItem value="email">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Email fontSize="small" />
                    Email
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Mensaje Personalizado (Opcional)"
              multiline
              rows={4}
              value={newNotification.customMessage}
              onChange={(e) => setNewNotification({
                ...newNotification,
                customMessage: e.target.value
              })}
              placeholder="Deja vacío para usar la plantilla predeterminada"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            disabled={sendingNotification}
          >
            {sendingNotification ? <CircularProgress size={20} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MeetingNotifications;